/**
 * Ngày/giờ theo lịch Việt Nam (Asia/Ho_Chi_Minh, UTC+7, không có giờ mùa hè) — dùng chung web và máy chủ.
 *
 * Vì sao cần: `date.toISOString().split('T')[0]` cho ngày theo UTC. Từ 00:00 đến 07:00 giờ VN đó là
 * HÔM QUA, và nửa đêm ngày 1 giờ VN thành ngày cuối tháng trước. Máy chủ production chạy Docker TZ=UTC
 * nên `setHours(0, 0, 0, 0)` / `new Date('YYYY-MM-DD')` cũng lệch 7 tiếng.
 * Mọi chỗ cần "ngày lịch của thời điểm này" hoặc "đầu/cuối ngày VN" phải đi qua các hàm ở đây
 * (scripts/scan-utc-date.cjs chặn mẫu cũ).
 */

export const VN_TIME_ZONE = 'Asia/Ho_Chi_Minh';
export const VN_UTC_OFFSET = '+07:00';
const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export type VnDateInput = Date | string | number | null | undefined;

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

let vnFormatter: Intl.DateTimeFormat | null | undefined;
function getFormatter(): Intl.DateTimeFormat | null {
  if (vnFormatter === undefined) {
    try {
      vnFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: VN_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    } catch {
      vnFormatter = null; // runtime thiếu dữ liệu múi giờ: dùng độ lệch cố định +7
    }
  }
  return vnFormatter;
}

interface VnParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function toDate(input: VnDateInput): Date | null {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function vnParts(d: Date): VnParts {
  const fmt = getFormatter();
  if (fmt) {
    const p: Record<string, string> = {};
    for (const part of fmt.formatToParts(d)) p[part.type] = part.value;
    return {
      year: Number(p.year),
      month: Number(p.month),
      day: Number(p.day),
      hour: Number(p.hour) % 24,
      minute: Number(p.minute),
      second: Number(p.second),
    };
  }
  const s = new Date(d.getTime() + VN_OFFSET_MS);
  return {
    year: s.getUTCFullYear(),
    month: s.getUTCMonth() + 1,
    day: s.getUTCDate(),
    hour: s.getUTCHours(),
    minute: s.getUTCMinutes(),
    second: s.getUTCSeconds(),
  };
}

const pad = (n: number, len = 2) => String(n).padStart(len, '0');
const ymd = (y: number, m: number, d: number) => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;

/**
 * Ngày lịch VN 'YYYY-MM-DD' của một thời điểm (mặc định: bây giờ).
 * Chuỗi 'YYYY-MM-DD' (ngày người dùng chọn) giữ nguyên. Giá trị rỗng/không hợp lệ → ''.
 */
export function toVnDateString(input: VnDateInput = new Date()): string {
  if (typeof input === 'string' && DATE_ONLY_RE.test(input)) return input;
  const d = toDate(input);
  if (!d) return '';
  const p = vnParts(d);
  return ymd(p.year, p.month, p.day);
}

/** 'YYYYMMDD' theo lịch VN — dùng cho mã chứng từ / số lô / tên file */
export function toVnDateCompact(input: VnDateInput = new Date()): string {
  return toVnDateString(input).replace(/-/g, '');
}

/** 'YYYY-MM' theo lịch VN */
export function toVnMonthString(input: VnDateInput = new Date()): string {
  return toVnDateString(input).slice(0, 7);
}

/** Giá trị cho <input type="datetime-local">: 'YYYY-MM-DDTHH:mm' theo giờ VN. Rỗng nếu không hợp lệ. */
export function toVnDateTimeLocal(input: VnDateInput = new Date()): string {
  const d = toDate(input);
  if (!d) return '';
  const p = vnParts(d);
  return `${ymd(p.year, p.month, p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

function parseDateOnly(s: string): { y: number; m: number; d: number } {
  const m = DATE_ONLY_RE.exec(s);
  if (!m) throw new Error(`Ngày không hợp lệ: ${s}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** Cộng/trừ số ngày trên chuỗi ngày lịch 'YYYY-MM-DD' (không phụ thuộc múi giờ máy) */
export function addDaysToDateString(dateStr: string, days: number): string {
  const { y, m, d } = parseDateOnly(dateStr);
  const t = new Date(Date.UTC(y, m - 1, d) + days * DAY_MS);
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Thứ Hai đầu tuần (lịch VN) chứa thời điểm/ngày đã cho */
export function vnStartOfWeek(input: VnDateInput = new Date()): string {
  const today = toVnDateString(input);
  if (!today) return '';
  const { y, m, d } = parseDateOnly(today);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = CN
  return addDaysToDateString(today, -((dow + 6) % 7));
}

/** Ngày 1 của tháng (lịch VN) */
export function vnStartOfMonth(input: VnDateInput = new Date()): string {
  const today = toVnDateString(input);
  return today ? `${today.slice(0, 7)}-01` : '';
}

/** Ngày đầu quý (lịch VN) */
export function vnStartOfQuarter(input: VnDateInput = new Date()): string {
  const today = toVnDateString(input);
  if (!today) return '';
  const { y, m } = parseDateOnly(today);
  return ymd(y, Math.floor((m - 1) / 3) * 3 + 1, 1);
}

/** Ngày 1/1 của năm (lịch VN) */
export function vnStartOfYear(input: VnDateInput = new Date()): string {
  const today = toVnDateString(input);
  return today ? `${today.slice(0, 4)}-01-01` : '';
}

/** Ngày cuối tháng của một ngày lịch 'YYYY-MM-DD' */
export function endOfMonthDateString(dateStr: string): string {
  const { y, m } = parseDateOnly(dateStr);
  const t = new Date(Date.UTC(y, m, 0));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Cộng/trừ số tháng, trả về ngày 1 của tháng đó: addMonthsToMonthStart('2026-01-15', -1) = '2025-12-01' */
export function addMonthsToMonthStart(dateStr: string, months: number): string {
  const { y, m } = parseDateOnly(dateStr);
  const t = new Date(Date.UTC(y, m - 1 + months, 1));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, 1);
}

/** 00:00:00.000 giờ VN của một ngày (chuỗi 'YYYY-MM-DD' hoặc thời điểm bất kỳ trong ngày đó) */
export function vnDayStart(input: VnDateInput = new Date()): Date {
  const day = toVnDateString(input);
  if (!day) throw new Error(`Ngày không hợp lệ: ${String(input)}`);
  return new Date(`${day}T00:00:00.000${VN_UTC_OFFSET}`);
}

/** 23:59:59.999 giờ VN của một ngày (chuỗi 'YYYY-MM-DD' hoặc thời điểm bất kỳ trong ngày đó) */
export function vnDayEnd(input: VnDateInput = new Date()): Date {
  const day = toVnDateString(input);
  if (!day) throw new Error(`Ngày không hợp lệ: ${String(input)}`);
  return new Date(`${day}T23:59:59.999${VN_UTC_OFFSET}`);
}

/**
 * Biên lọc theo ngày do client gửi lên (máy chủ dùng):
 * - 'YYYY-MM-DD' hoặc 'YYYY-MM-DDT00:00:00(.000)Z' (ngày chọn trên lịch bị đổi sang ISO) → đầu/cuối NGÀY VN đó;
 * - thời điểm đầy đủ khác (có giờ) → giữ nguyên thời điểm;
 * - rỗng/không hợp lệ → undefined.
 */
export function parseVnDateBoundary(value: VnDateInput, edge: 'start' | 'end'): Date | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const raw = value instanceof Date ? (Number.isNaN(value.getTime()) ? '' : value.toISOString()) : String(value).trim();
  const m = /^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(raw);
  if (m) return edge === 'start' ? vnDayStart(m[1]) : vnDayEnd(m[1]);
  const d = toDate(raw);
  return d ?? undefined;
}

/** Khoảng lọc {gte, lte} theo ngày VN; undefined nếu cả hai biên rỗng */
export function vnDateRangeFilter(from: VnDateInput, to: VnDateInput): { gte?: Date; lte?: Date } | undefined {
  const gte = parseVnDateBoundary(from, 'start');
  const lte = parseVnDateBoundary(to, 'end');
  if (!gte && !lte) return undefined;
  return { ...(gte ? { gte } : {}), ...(lte ? { lte } : {}) };
}

/** Các khoá kỳ lọc được hỗ trợ (không phân biệt hoa thường; 'thisMonth' = 'month' ...) */
export const VN_PERIOD_KEYS = ['today', 'yesterday', 'week', 'month', 'quarter', 'year', 'last7days', 'last30days'] as const;
export type VnPeriodKey = (typeof VN_PERIOD_KEYS)[number];

/** Chuẩn hoá khoá kỳ: 'thisMonth' → 'month', 'last30Days' → 'last30days'; không nhận ra → null */
export function normalizeVnPeriodKey(period?: string | null): VnPeriodKey | null {
  if (!period) return null;
  const k = period.trim().toLowerCase().replace(/^this/, '').replace(/[\s_-]/g, '');
  return (VN_PERIOD_KEYS as readonly string[]).includes(k) ? (k as VnPeriodKey) : null;
}

/**
 * Khoảng ngày lịch VN {from, to} ('YYYY-MM-DD') cho một kỳ, tính đến hôm nay (to = hôm nay, trừ 'yesterday').
 * Khoá không hỗ trợ → null.
 */
export function vnPeriodRange(period: string | null | undefined, now: VnDateInput = new Date()): { from: string; to: string } | null {
  const key = normalizeVnPeriodKey(period);
  const today = toVnDateString(now);
  if (!key || !today) return null;
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDaysToDateString(today, -1);
      return { from: y, to: y };
    }
    case 'week':
      return { from: vnStartOfWeek(today), to: today };
    case 'month':
      return { from: vnStartOfMonth(today), to: today };
    case 'quarter':
      return { from: vnStartOfQuarter(today), to: today };
    case 'year':
      return { from: vnStartOfYear(today), to: today };
    case 'last7days':
      return { from: addDaysToDateString(today, -6), to: today };
    case 'last30days':
      return { from: addDaysToDateString(today, -29), to: today };
  }
}

/** Biên tháng theo lịch VN: [00:00 giờ VN ngày 1, 00:00 giờ VN ngày 1 tháng sau) — dùng gte/lt */
export function vnMonthRange(year: number, month: number): { start: Date; endExclusive: Date } {
  const first = ymd(year, month, 1);
  return { start: vnDayStart(first), endExclusive: vnDayStart(addMonthsToMonthStart(first, 1)) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Thành phần lịch / giờ treo tường VN của một thời điểm (thay cho getFullYear/getMonth/getDate/getDay/
// getHours/setHours/setDate — các hàm này đọc múi giờ của TIẾN TRÌNH, production là UTC).
// VN không có giờ mùa hè nên "cộng N ngày" = cộng N × 24 giờ và giữ nguyên giờ treo tường.
// ─────────────────────────────────────────────────────────────────────────────

export interface VnDateParts {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = Chủ Nhật … 6 = Thứ Bảy */
  dayOfWeek: number;
}

/** Thành phần lịch VN của một thời điểm (mặc định: bây giờ). Không hợp lệ → mọi trường là NaN. */
export function vnDateParts(input: VnDateInput = new Date()): VnDateParts {
  if (typeof input === 'string' && DATE_ONLY_RE.test(input)) {
    const { y, m, d } = parseDateOnly(input);
    return { year: y, month: m, day: d, hour: 0, minute: 0, second: 0, dayOfWeek: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
  }
  const d = toDate(input);
  if (!d) return { year: NaN, month: NaN, day: NaN, hour: NaN, minute: NaN, second: NaN, dayOfWeek: NaN };
  // Độ lệch cố định +7 (VN không đổi giờ từ 1975) — nhanh, dùng được trong vòng lặp lịch ca
  const s = new Date(d.getTime() + VN_OFFSET_MS);
  return {
    year: s.getUTCFullYear(),
    month: s.getUTCMonth() + 1,
    day: s.getUTCDate(),
    hour: s.getUTCHours(),
    minute: s.getUTCMinutes(),
    second: s.getUTCSeconds(),
    dayOfWeek: s.getUTCDay(),
  };
}

/** Năm theo lịch VN */
export const vnYear = (input: VnDateInput = new Date()): number => vnDateParts(input).year;
/** Tháng theo lịch VN, 1-12 (KHÁC getMonth() là 0-11) */
export const vnMonth = (input: VnDateInput = new Date()): number => vnDateParts(input).month;
/** Ngày trong tháng theo lịch VN */
export const vnDayOfMonth = (input: VnDateInput = new Date()): number => vnDateParts(input).day;
/** Thứ trong tuần theo lịch VN: 0 = Chủ Nhật … 6 = Thứ Bảy */
export const vnDayOfWeek = (input: VnDateInput = new Date()): number => vnDateParts(input).dayOfWeek;
/** Giờ (0-23) theo giờ VN */
export const vnHour = (input: VnDateInput = new Date()): number => vnDateParts(input).hour;
/** Số phút kể từ 00:00 giờ VN */
export function vnMinutesOfDay(input: VnDateInput = new Date()): number {
  const p = vnDateParts(input);
  return p.hour * 60 + p.minute;
}

/** 'YY' theo lịch VN (2 số cuối của năm) — tiền tố mã chứng từ */
export function vnYY(input: VnDateInput = new Date()): string {
  const y = vnYear(input);
  return Number.isNaN(y) ? '' : pad(y % 100);
}
/** 'MM' theo lịch VN */
export function vnMM(input: VnDateInput = new Date()): string {
  const m = vnMonth(input);
  return Number.isNaN(m) ? '' : pad(m);
}
/** 'YYMM' theo lịch VN — tiền tố mã chứng từ (PXK-2610-…) */
export const vnYYMM = (input: VnDateInput = new Date()): string => `${vnYY(input)}${vnMM(input)}`;
/** 'YYMMDD' theo lịch VN */
export const vnYYMMDD = (input: VnDateInput = new Date()): string => toVnDateCompact(input).slice(2);

const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Thời điểm của giờ treo tường VN trong một ngày lịch: vnWallTimeToInstant('2026-10-24', '07:30') = 2026-10-24T00:30:00Z.
 * `date` là chuỗi 'YYYY-MM-DD' hoặc một thời điểm (lấy ngày VN của nó). `time` là 'HH:mm', 'HH:mm:ss' hoặc số giờ.
 */
export function vnWallTimeToInstant(date: VnDateInput, time: string | number = '00:00', minute = 0): Date {
  const day = toVnDateString(date);
  if (!day) throw new Error(`Ngày không hợp lệ: ${String(date)}`);
  let h: number;
  let mi: number;
  let s = 0;
  if (typeof time === 'number') {
    h = time;
    mi = minute;
  } else {
    const m = TIME_RE.exec(time.trim());
    if (!m) throw new Error(`Giờ không hợp lệ: ${time}`);
    h = Number(m[1]);
    mi = Number(m[2]);
    s = m[3] ? Number(m[3]) : 0;
  }
  return new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)), h, mi, s) - VN_OFFSET_MS);
}

/** Cùng ngày lịch VN với `input`, đặt giờ treo tường VN = hour:minute:second.ms (thay cho setHours) */
export function vnSetTime(input: VnDateInput, hour: number, minute = 0, second = 0, ms = 0): Date {
  const base = vnWallTimeToInstant(input, hour, minute);
  return new Date(base.getTime() + second * 1000 + ms);
}

/** Cộng/trừ N ngày lịch, giữ nguyên giờ treo tường VN (thay cho setDate(getDate() + n)) */
export function vnAddDays(input: VnDateInput, days: number): Date {
  const d = toDate(input);
  if (!d) throw new Error(`Thời điểm không hợp lệ: ${String(input)}`);
  return new Date(d.getTime() + days * DAY_MS);
}

/** Cộng/trừ N tháng lịch VN, giữ giờ treo tường; ngày vượt cuối tháng được kẹp về ngày cuối tháng (31/1 + 1 tháng = 28/2) */
export function vnAddMonths(input: VnDateInput, months: number): Date {
  const d = toDate(input);
  if (!d) throw new Error(`Thời điểm không hợp lệ: ${String(input)}`);
  const p = vnParts(d);
  const first = addMonthsToMonthStart(ymd(p.year, p.month, 1), months);
  const lastDay = Number(endOfMonthDateString(first).slice(8, 10));
  const day = `${first.slice(0, 8)}${pad(Math.min(p.day, lastDay))}`;
  return new Date(vnWallTimeToInstant(day, p.hour, p.minute).getTime() + (d.getTime() % 60000 + 60000) % 60000);
}

/** Số ngày lịch VN từ `from` đến `to` (to - from); 23:59 hôm nay → 00:01 ngày mai = 1 */
export function vnCalendarDaysBetween(from: VnDateInput, to: VnDateInput): number {
  const a = toVnDateString(from);
  const b = toVnDateString(to);
  if (!a || !b) return NaN;
  const pa = parseDateOnly(a);
  const pb = parseDateOnly(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / DAY_MS);
}

/** 'dd/MM/yyyy' theo lịch VN (thay cho toLocaleDateString('vi-VN') không có timeZone) */
export function formatVnDate(input: VnDateInput = new Date()): string {
  const s = toVnDateString(input);
  return s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '';
}

/** 'dd/MM/yyyy HH:mm' theo giờ VN */
export function formatVnDateTime(input: VnDateInput = new Date()): string {
  const s = toVnDateTimeLocal(input);
  return s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} ${s.slice(11, 16)}` : '';
}
